/**
 * Exact-input reactions (D27): the pack's authored one-liners for inputs the
 * parser cannot place — easter eggs as a platform feature.
 *
 * @remarks
 * A reaction maps one EXACT player input (normalized: character-folded,
 * lowercased, whitespace-collapsed — the parser's own `foldCharacters`
 * treatment) onto one authored narrative line. The engine consults the table
 * ONLY when the whole input failed to parse, so a reaction can never shadow a
 * real command — "dude" earns "Sweet."; "look" can never be stolen from the
 * world. Reactions consume no turn and change no state beyond the transcript:
 * they are voice, not mechanics.
 *
 * Keys are validated to be non-empty and already-lowercase (the folded form
 * players' input is compared against); authoring "DUDE" would silently never
 * match, so the schema refuses it loudly instead. The richer machinery this
 * table foreran — conditions, once-per-session firing, occurrence matchers —
 * landed as Phase 3's triggers (D71/D72), and the promise held: this table
 * remains the exact-input sugar in front of it, subsumed, not broken (a
 * spoken reaction is itself an occurrence triggers can listen for).
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { narrativeLineSchema } from './transcript.js';

/** One reaction key: non-empty, and already in the folded/lowercase form the engine compares against. */
const reactionKeySchema = z
	.string()
	.trim()
	.min(1)
	.refine((key) => key === key.toLowerCase(), {
		message: 'reaction keys must be lowercase — input is folded to lowercase before lookup, so an uppercase key can never match',
	});

/** The pack's exact-input reaction table (D27): normalized input → the authored line. */
export const reactionResponsesSchema = z.record(reactionKeySchema, narrativeLineSchema).readonly();

/** The authored reaction table, inferred. */
export type ReactionResponses = z.infer<typeof reactionResponsesSchema>;
